import {findNodeById} from "../misc/utils"
import _ from "lodash"
import moment from "moment"
import {nanoid} from "nanoid"

const statusValueMap = Object.freeze({
    NICHTABGESICHERT: 0,
    ABGESICHERT: 1,
    GUTABGESICHERT: 1,
})

const transformProductsWithSpartenToSpartenWithProducts = (products) => {
    const spartenMap = new Map()
    products.forEach((product) => {
        const {connections, ...productWithoutConnections} = product
        connections?.forEach((conn) => {
            const sparte = conn.origin
            const sparteId = sparte.id

            if (!spartenMap.has(sparteId)) {
                spartenMap.set(sparteId, {
                    ...sparte,
                    produkte: [],
                })
            }

            spartenMap.get(sparteId).produkte.push(productWithoutConnections)
        })
    })

    return Array.from(spartenMap.values())
}

const createSollProdukte = (spartenArray, merkmalBaum) => {
    return spartenArray.map((sparte) => {
        const sollMerkmale = {}

        sparte.produkte.forEach((produkt) => {
            const merkmale = produkt.merkmale || {}

            Object.entries(merkmale).forEach(([id, values]) => {
                if (!sollMerkmale[id]) {
                    sollMerkmale[id] = {...values}
                    if (values.select)
                        sollMerkmale[id].select = new Set(values.select)
                    return
                }

                const target = sollMerkmale[id]

                if (values.from !== undefined) {
                    target.from =
                        target.from === undefined
                            ? values.from
                            : Math.min(
                                  Number(target.from),
                                  Number(values.from),
                              ).toString()
                }

                if (values.till !== undefined) {
                    target.till =
                        target.till === undefined
                            ? values.till
                            : Math.max(
                                  Number(target.till),
                                  Number(values.till),
                              ).toString()
                }

                if (values.select) {
                    if (!target.select) target.select = new Set()
                    values.select.forEach((item) => target.select.add(item))
                }

                if (values.gewicht !== undefined) {
                    target.gewicht = Math.max(
                        Number(target.gewicht || 0),
                        Number(values.gewicht),
                    ).toString()
                }
            })
        })

        Object.keys(sollMerkmale).forEach((id) => {
            if (sollMerkmale[id].select instanceof Set) {
                sollMerkmale[id].select = Array.from(sollMerkmale[id].select)
            }
            const merkmal = findNodeById(id, merkmalBaum)
            sollMerkmale[id].origin = merkmal
            sollMerkmale[id].property = merkmal?.pointer?.split("/").pop()
        })

        return {
            ...sparte,
            merkmale: sollMerkmale,
        }
    })
}


const filterDataByRules = (inputData = [], rulesConfig) => {
    const dataArray = inputData ?? []
    const activeRules = Object.values(rulesConfig)
        .filter((rule) => +rule.gewicht > 0 && rule.value)
        .map((rule) => ({
            value: typeof rule.value === "boolean" ? rule.value : null,
            property: rule.property,
            from: rule.from ? Number(rule.from) : null,
            till: rule.till ? Number(rule.till) : null,
            select: rule.select || null,
        }))
    return (
        dataArray?.filter((item) => {
            return activeRules.every((rule) => {
                const value = item?.[rule.property]
                if(rule.property in item && value === undefined) {
                    return false
                }

                if (value === undefined) {
                    return true
                }

                if (rule.from !== null || rule.till !== null) {
                    const numericValue =
                        typeof value === "string"
                            ? parseFloat(value.replace(/[^0-9.]/g, ""))
                            : value
                    if (rule.from !== null && numericValue < rule.from)
                        return false
                    if (rule.till !== null && numericValue > rule.till)
                        return false
                }

                if (rule.select !== null && !rule.select.includes(value)) {
                    return false
                }

                if (typeof value === "boolean") {
                    return rule.value === value
                }

                return true
            })
        }) ?? []
    )
}

const mockProdukte = [
    {
        label: "Hausrat Basis",
        preis: 663,
        datum: moment().format("YYYY-MM-DD"),
        status: "Antrag",
        isTopprodukt: false,
        isEmpfehlung: false,
        isNeitherTopNorEmpfehlung: true,
        topIsEmpfehlung: false,
        leistungsmerkmal: {
            versicherungssumme: true,
            wertsachen: true,
            neuwertversicherung: true,
            schaeden: true,
            außenversicherung: true,
            blitzueberspannung: false,
            fahrlaessigkeit: false,
            diebstahl: false,
            garteninventar: false,
            arbeitsplatz: false,
        },
        zusatzschutz: {
            naturgefahr: false,
            glasversicherung: false,
            fahraddiebstahl: false,
            selbstbehalt: "250",
            vertragslaufzeit: "1",
            zahlweise: "MONATLICH",
        },
    },
    {
        label: "Hausrat Komfort",
        preis: 12.45,
        datum: moment().format("YYYY-MM-DD"),
        status: "Antrag",
        isTopprodukt: false,
        isEmpfehlung: false,
        isNeitherTopNorEmpfehlung: true,
        topIsEmpfehlung: false,
        leistungsmerkmal: {
            versicherungssumme: true,
            wertsachen: true,
            neuwertversicherung: true,
            schaeden: true,
            außenversicherung: true,
            blitzueberspannung: true,
            fahrlaessigkeit: true,
            diebstahl: true,
            garteninventar: false,
            arbeitsplatz: false,
        },
        zusatzschutz: {
            naturgefahr: false,
            glasversicherung: false,
            fahraddiebstahl: false,
            selbstbehalt: "250",
            vertragslaufzeit: "1",
            zahlweise: "MONATLICH",
        },
    },
    {
        label: "Hausrat Premium",
        preis: 18.9,
        datum: moment().format("YYYY-MM-DD"),
        status: "Antrag",
        isTopprodukt: true,
        isEmpfehlung: false,
        isNeitherTopNorEmpfehlung: false,
        topIsEmpfehlung: false,
        leistungsmerkmal: {
            versicherungssumme: true,
            wertsachen: true,
            neuwertversicherung: true,
            schaeden: true,
            außenversicherung: true,
            blitzueberspannung: true,
            fahrlaessigkeit: true,
            diebstahl: true,
            garteninventar: true,
            arbeitsplatz: true,
        },
        zusatzschutz: {
            naturgefahr: false,
            glasversicherung: false,
            fahraddiebstahl: false,
            selbstbehalt: "250",
            vertragslaufzeit: "1",
            zahlweise: "MONATLICH",
        },
    },
]

export const CockpitActions = ({
    akJson,
    getDataFromPointer,
    updateData,
    navigate,
}) => {
    const evaluateAbsicherungen = () => {
        const rawAnlaesse = akJson.data.anlassbaum.find(
            (entry) => entry.label === "Startseite",
        )?.children

        const anlaesse = rawAnlaesse.map((entry) => ({
            ...entry,
            text: entry.langname,
            dataType: "widget",
        }))
        const rawThemenfelder = akJson.data.themenfelder.find(
            (entry) => entry.label === "beratung",
        )?.children
        const rawThemenfelderFirma = akJson.data.themenfelder.find(
            (entry) => entry.id === "yF05GBkmhSpvkFWh",
        )?.children
        const rawAnlassThemenfelder = akJson.data.themenfelder.find(
            (entry) => entry.label === "anlässe",
        )?.children
        const rawProdukte = akJson.data.produkte.flatMap(
            (produktgeber) => produktgeber.children,
        )

        const spartenWithProducts =
            transformProductsWithSpartenToSpartenWithProducts(rawProdukte)
        const sollProdukte = createSollProdukte(
            spartenWithProducts,
            akJson.data.attributes,
        )
        const absicherungList = getDataFromPointer("/absicherung/list") ?? []

        const getLeistungsmerkmale = (sparte) => {
            return sparte.children?.flatMap(
                (entry) =>
                    entry.leistungsmerkmale?.map((item) => item.origin) ?? [],
            )
        }

        const getObjekte = (sparte) => {
            const objekte = sparte.children?.flatMap(
                (entry) => entry.connections?.map((item) => item.origin) ?? [],
            )

            return objekte.map((objekt) => {
                const data = getDataFromPointer(objekt.pointer)
                if (!data) return []
                return {
                    ...objekt,
                    data: _.isArray(data) ? data : [data],
                }
            })
        }

        const getAbgesichertStatus = (sparte, object, list) => {
            const absicherung = list.find(
                (entry) =>
                    entry.produkt?.id === sparte?.id &&
                    _.isEqual(entry.objekt, object),
            )
            return !absicherung ? "NICHTABGESICHERT" : absicherung.status
        }
        const getAbgesicherungen = (sparte, object, list) => {
            const absicherung = list.find(
                (entry) =>
                    entry.produkt?.id === sparte?.id &&
                    _.isEqual(entry.objekt, object),
            )
            return absicherung
        }

        const newAbsicherungList =
            getDataFromPointer("/absicherung/newList") ?? []

        const createSpartenEntry = (sparte, objekt) => ({
            ...sparte,
            important: sparte?.prio,
            status: getAbgesichertStatus(sparte, objekt, absicherungList),
            newStatus: getAbgesichertStatus(sparte, objekt, newAbsicherungList),
            newProduct: getAbgesicherungen(sparte, objekt, newAbsicherungList),
            leistungsmerkmale: getLeistungsmerkmale(sparte),
            objekt,
        })

        const sollMerkmaleErfuellt = (merkmale) => {
            const rules = Object.values(merkmale).filter(
                (rule) =>
                    +rule.gewicht > 0 &&
                    (rule.value !== undefined ||
                        rule.select !== undefined ||
                        rule.from !== undefined ||
                        rule.till !== undefined),
            )
            if (!rules.length) return false
            return rules.every((rule) => {
                const answer = getDataFromPointer(rule.origin?.pointer)
                if (answer === undefined || answer === null || answer === "") {
                    return false
                }
                if (rule.select) {
                    return rule.select.includes(answer)
                }
                if (rule.from !== undefined || rule.till !== undefined) {
                    const num =
                        typeof answer === "string"
                            ? parseFloat(answer.replace(/[^0-9.]/g, ""))
                            : answer
                    if (rule.from !== undefined && num < Number(rule.from))
                        return false
                    if (rule.till !== undefined && num > Number(rule.till))
                        return false
                    return true
                }
                return answer === rule.value
            })
        }

        const enrichSparte = (sparte) => {
            const currentSollProdukt = sollProdukte.find(
                (entry) => entry.id === sparte.id,
            )
            if (!currentSollProdukt) return []

            const objektEntries = getObjekte(sparte).filter((entry) =>
                entry?.data?.some(
                    (item) => item !== null && typeof item === "object",
                ),
            )
            if (objektEntries.length) {
                return objektEntries.flatMap((entry) => {
                    const objekte = entry.data.filter(
                        (item) => item !== null && typeof item === "object",
                    )
                    return filterDataByRules(
                        objekte,
                        currentSollProdukt.merkmale,
                    ).map((objekt) => createSpartenEntry(sparte, objekt))
                })
            }

            return sollMerkmaleErfuellt(currentSollProdukt.merkmale)
                ? [createSpartenEntry(sparte, null)]
                : []
        }

        const themenfelder = rawThemenfelder.map((entry) => ({
            ...entry,
            text: entry.longname,
            link: "/" + entry.label,
            sparten: entry.connections?.flatMap((spartenConnection) =>
                enrichSparte(spartenConnection.origin),
            ),
            dataType: "widget",
        }))

        const themenfelderFirma = rawThemenfelderFirma.map((entry) => ({
            ...entry,
            text: entry.longname,
            link: "/" + entry.label,
            sparten: entry.connections?.flatMap((spartenConnection) =>
                enrichSparte(spartenConnection.origin),
            ),
            dataType: "widget",
        }))

        const anlassThemenfelder = rawAnlassThemenfelder?.map((entry) => ({
            ...entry,
            text: entry.longname,
            link: "/" + entry.label,
            sparten: entry.connections?.flatMap((spartenConnection) =>
                enrichSparte(spartenConnection.origin),
            ),
            dataType: "widget",
        })) ?? []
        const currentThemenfeldId = getDataFromPointer(
            "/appstate/selectedThemenfeld",
        )?.id
        const allSpartenWithObjects =
            akJson.data.spartenbaum.flatMap(enrichSparte)

        const allThemenfelder = [...themenfelder, ...themenfelderFirma, ...anlassThemenfelder]
        const selectedThemenfeld = currentThemenfeldId
            ? allThemenfelder.find((entry) => entry.id === currentThemenfeldId)
            : {
                  sparten: allThemenfelder.reduce(
                      (prev, curr) => [...prev, ...(curr?.sparten ?? [])],
                      [],
                  ),
              }

        updateData(anlaesse, "/anlaesse")
        updateData(themenfelder, "/cards")
        updateData(themenfelderFirma, "/cardsFirma")
        updateData(anlassThemenfelder, "/anlassCards")
        updateData(selectedThemenfeld, "/themenfeld")
        updateData(akJson.data.spartenbaum, "/sparten")
        updateData(allSpartenWithObjects, "/spartenWithObjects/list")
    }

    const tarifrechnen = () => {
        const objektData = getDataFromPointer("/taa/versichertesobjekt")
        const zahlweise = getDataFromPointer("/taa/angebotsdaten/zahlweise")
        const {
            wohnflaeche = 5,
            versicherungssumme = 5,
            haustype = "WOHNUNG",
        } = objektData ?? {}

        const grundbeitrag = 2.5
        const flaechenFaktor = 0.08
        const versicherungsSummenFaktor = 0.00015
        const fahrradschutzBeitrag = 2

        const immobilienFaktor = haustype === "HAUS" ? 1 : 0

        const flaeche = Number(wohnflaeche) || 0
        const vSumme = Number(versicherungssumme) || 0
        const mindestVSumme = 600 * flaeche
        const effektiveVSumme = Math.max(vSumme, mindestVSumme)
        const mindestbeitrag =
            (grundbeitrag +
                flaechenFaktor * flaeche +
                versicherungsSummenFaktor * effektiveVSumme) *
            (1 + 0.2 * immobilienFaktor)

        const selbstbehaltFaktorMap = {
            0: 1,
            250: 0.95,
            500: 0.9,
            1000: 0.85,
            2500: 0.8,
        }

        const tierMultiplierMap = {Basis: 1, Komfort: 1.25, Premium: 1.4}

        const targetProdukte = getDataFromPointer("/taa/produkt/list")
        const updatedProdukte = targetProdukte.map((produkt) => {
            const {
                naturgefahr,
                glasversicherung,
                fahraddiebstahl,
                selbstbehalt,
                vertragslaufzeit,
            } = produkt.zusatzschutz
            const naturgefahrFaktor = naturgefahr ? 1 : 0
            const glasFaktor = glasversicherung ? 1 : 0
            const fahrradFaktor = fahraddiebstahl ? 1 : 0
            const selbstbehaltFaktor = selbstbehaltFaktorMap[selbstbehalt] ?? 1
            const zahlweiseFaktor = zahlweise === "MONATLICH" ? 0 : 1
            const vertragslaufzeitFaktor = vertragslaufzeit === "1" ? 1 : 0.9
            const tier = Object.keys(tierMultiplierMap).find((key) =>
                produkt.label.includes(key),
            )
            const tierMultiplier = tierMultiplierMap[tier] ?? 1

            const beitrag =
                (mindestbeitrag *
                    tierMultiplier *
                    (1 + 0.2 * naturgefahrFaktor) *
                    (1 + 0.2 * glasFaktor) *
                    selbstbehaltFaktor *
                    vertragslaufzeitFaktor +
                    fahrradschutzBeitrag * fahrradFaktor) *
                (1 + 10 * zahlweiseFaktor)

            return {...produkt, preis: Math.round(beitrag * 100) / 100}
        })

        updateData(updatedProdukte, "/taa/produkt/list")
    }

    const evaluateProducts = () => {
        const prueffragen = getDataFromPointer("/taa/prueffragen")
        if (prueffragen.garteninventar || prueffragen.arbeitsplatz) {
            return [
                {...mockProdukte[2],
                    isEmpfehlung: true}
            ]
        }
        if (
            prueffragen.blitzueberspannung ||
            prueffragen.fahrlaessigkeit ||
            prueffragen.diebstahl
        ) {
            return [{...mockProdukte[1], isEmpfehlung: true, isNeitherTopNorEmpfehlung: false}, mockProdukte[2]]
        }

        return [{...mockProdukte[0], isEmpfehlung: true, isNeitherTopNorEmpfehlung: false}, mockProdukte[1]]
    }

    const evaluateProductsInViewAll = () => {
        let empfehlung
        const prueffragen = getDataFromPointer("/taa/prueffragen")
        if (prueffragen.garteninventar || prueffragen.arbeitsplatz) {
                empfehlung = 2
            return mockProdukte.map((product, index) => index === empfehlung ? {...product, isEmpfehlung: true, isNeitherTopNorEmpfehlung: false } : {...product, topIsEmpfehlung: true } )
        } else if (
            prueffragen.blitzueberspannung ||
            prueffragen.fahrlaessigkeit ||
            prueffragen.diebstahl
        ) {
            empfehlung = 1
        } else {
            empfehlung = 0
        }

        return mockProdukte.map((product, index) => index === empfehlung ? {...product, isEmpfehlung: true, isNeitherTopNorEmpfehlung: false } : product)
    }

    return {
        INIT: () => {
            const rawAnlaesse = akJson.data.anlassbaum.find(
                (entry) => entry.label === "Startseite",
            )?.children
            const anlaesse = rawAnlaesse.map((entry) => ({
                ...entry,
                text: entry.langname,
                dataType: "widget",
            }))
            const rawThemenfelder = akJson.data.themenfelder.find(
                (entry) => entry.label === "beratung",
            )?.children
            const rawAnlassThemenfelder = akJson.data.themenfelder.find(
                (entry) => entry.label === "anlässe",
            )?.children

            const getLeistungsmerkmale = (sparte) => {
                return sparte.children?.flatMap(
                    (entry) =>
                        entry.leistungsmerkmale?.map((item) => item.origin) ??
                        [],
                )
            }

            const themenfelder = rawThemenfelder.map((entry) => ({
                ...entry,
                text: entry.longname,
                link: "/" + entry.label,
                sparten: entry.connections?.flatMap((sparte) => ({
                    ...sparte.origin,
                    important: sparte.origin?.prio,
                    status: "ABGESICHERT",
                    newStatus: "LEER",
                    leistungsmerkmale: getLeistungsmerkmale(sparte.origin),
                })),
                dataType: "widget",
            }))

            const anlassThemenfelder = rawAnlassThemenfelder?.map((entry) => ({
                ...entry,
                text: entry.longname,
                link: "/" + entry.label,
                sparten: entry.connections?.flatMap((sparte) => ({
                    ...sparte.origin,
                    important: sparte.origin?.prio,
                    status: "ABGESICHERT",
                    newStatus: "LEER",
                    leistungsmerkmale: getLeistungsmerkmale(sparte.origin),
                })),
                dataType: "widget",
            })) ?? []

            const selectedThemenfeld = themenfelder[1]
            updateData(anlaesse, "/anlaesse")
            updateData(anlaesse, "/anlaesseFirma")
            updateData(themenfelder, "/cards")
            updateData(themenfelder, "/cardsFirma")
            updateData(anlassThemenfelder, "/anlassCards")
            updateData(anlassThemenfelder, "/anlassCardsFirma")
            updateData(selectedThemenfeld, "/themenfeld")
            updateData(selectedThemenfeld, "/themenfeldFirma")
        },
        INIT_ABSICHERUNGSPROFIL: () => {
            updateData(false, "/pageState/absicherungsprofilDialogOpen")
            updateData(false, "/pageState/addAbsicherungDialogOpen")
            evaluateAbsicherungen()
        },
        OPEN_QUESTIONAIRE: (payload) => {
            updateData(true, "/pageState/fragenDialogOpen")
            updateData(
                payload.questionaire.origin,
                "/pageState/fragenDialogQuestionaire",
            )
        },
        CLOSE_QUESTIONAIRE_DIALOG: () => {
            updateData(false, "/pageState/fragenDialogOpen")
        },
        EVALUATE_THEMENFELDER: () => {
            evaluateAbsicherungen()
            const themenfelder = getDataFromPointer("/cards")
            const calcProgress = (themenfeld) => {
                if (!themenfeld.sparten) {
                    return 0
                }
                const score = themenfeld.sparten?.reduce(
                    (prev, curr) =>
                        prev +
                        Math.max(
                            statusValueMap[curr.status] ?? 0,
                            statusValueMap[curr.newStatus] ?? 0,
                        ),
                    0,
                )
                return (score * 100) / themenfeld.sparten.length
            }

            const evaluatedThemenfelder = themenfelder.map((themenfeld) => ({
                ...themenfeld,
                progress: calcProgress(themenfeld),
            }))
            updateData(evaluatedThemenfelder, "/cards")
        },
        EVALUATE_THEMENFELDER_FIRMENKUNDEN: () => {
            evaluateAbsicherungen()
            const themenfelder = getDataFromPointer("/cardsFirma")
            const calcProgress = (themenfeld) => {
                if (!themenfeld.sparten) {
                    return 0
                }
                const score = themenfeld.sparten?.reduce(
                    (prev, curr) =>
                        prev +
                        Math.max(
                            statusValueMap[curr.status] ?? 0,
                            statusValueMap[curr.newStatus] ?? 0,
                        ),
                    0,
                )
                return (score * 100) / themenfeld.sparten.length
            }

            const evaluatedThemenfelder = themenfelder.map((themenfeld) => ({
                ...themenfeld,
                progress: calcProgress(themenfeld),
            }))
            updateData(evaluatedThemenfelder, "/cardsFirma")
        },
        ABSICHERUNGSPROFIL_OPEN_DIALOG: () => {
            updateData(true, "/pageState/absicherungsprofilDialogOpen")
        },
        ABSICHERUNGSPROFIL_OPEN_DIALOG_ADD_ABSICHERUNG: () => {
            updateData(false, "/pageState/absicherungsprofilDialogAdd")
            updateData(true, "/pageState/addAbsicherungDialogOpen")
        },
        SELECT_THEMENFELD: (payload) => {
            updateData(payload, "/themenfeld")
        },
        SELECT_THEMENFELD_FIRMENKUNDE: (payload) => {
            updateData(payload, "/themenfeld")
        },
        INIT_DOWNLOAD_FILE: () => {
            const verkaufsunterlagen = Object.values(akJson.images.datei)
            updateData(verkaufsunterlagen, "/appstate/verkaufsunterlagen")
        },
        ABSICHERUNGSPROFIL_GET_OBJECTS_FOR_SPARTE: ({value}) => {
            const list = getDataFromPointer("/spartenWithObjects/list") ?? []
            const objectList = list
                .filter((entry) => entry.id === value.id)
                .map((entry) => entry.objekt)
            updateData(objectList, "/spartenWithObjects/selected")
        },
        ABSICHERUNGSPROFIL_ABORT_ADD_ABSICHERUNG: () => {
            updateData(false, "/pageState/absicherungsprofilDialogAdd")
        },
        ABSICHERUNGSPROFIL_ADD_ABSICHERUNG: () => {
            const newAbsicherung = getDataFromPointer("/absicherung/selected")
            const absicherungList =
                getDataFromPointer("/absicherung/list") ?? []
            if (newAbsicherung.id) {
                const updatedList = absicherungList.map((item) =>
                    item.id === newAbsicherung.id ? newAbsicherung : item,
                )
                updateData(updatedList, "/absicherung/list")
            } else {
                newAbsicherung.id = nanoid()
                updateData(
                    [...absicherungList, newAbsicherung],
                    "/absicherung/list",
                )
            }

            updateData(false, "/pageState/absicherungsprofilDialogAdd")
        },
        ABSICHERUNGSPROFIL_EDIT_ABSICHERUNG: ({payload}) => {
            updateData(payload, "/absicherung/selected")
            updateData(true, "/pageState/absicherungsprofilDialogAdd")
        },
        ABSICHERUNGSPROFIL_DELETE_ABSICHERUNG: ({payload}) => {
            const absicherungList =
                getDataFromPointer("/absicherung/list") ?? []
            const updatedList = absicherungList.filter(
                (item) => item.id !== payload.id,
            )
            updateData(updatedList, "/absicherung/list")
        },
        ABSICHERUNGSPROFIL_OPEN_ADD_ABSICHERUNG: () => {
            updateData(true, "/pageState/absicherungsprofilDialogAdd")
            updateData({}, "/absicherung/selected")
        },
        OPEN_ELEMENTARGEFAHREN_DIALOG: () => {
            updateData(true, "/appstate/openElementargefahrenBildDialog")
        },
        CLOSE_ELEMENTARGEFAHREN_DIALOG: () => {
            updateData(false, "/appstate/openElementargefahrenBildDialog")
        },
        ABSICHERUNGSPROFIL_CREATE_ANGEBOT: ({payload}) => {
            const selectedObjekte = payload.filter((entry) => entry.isSelected)
            updateData(selectedObjekte, "/themenfeld/angebotsobjekte")
            navigate("/absicherungangebot")
        },
        ABSICHERUNGSPROFIL_START_TAA: ({payload}) => {
            const mock = {
                id: "WEHYurmMOhXz95hP",
                children: [
                    {
                        id: "ffsH73I0xBbROlbM",
                        children: [],
                        label: "Wohngebäudeversicherungen",
                        type: "leafNode",
                        active: true,
                        longname: "Wohngebäudeversicherungen",
                        connections: [
                            {
                                id: "nJFbeqxw1jlQkXd7",
                                connectionType: "multiconnection",
                                treeName: "attributes",
                                origin: {
                                    id: "nJFbeqxw1jlQkXd7",
                                    children: [
                                        {
                                            id: "UVpSAbG762bk1f6W",
                                            children: [],
                                            label: "Adresse",
                                            type: "leafNode",
                                            active: true,
                                            antworttyp: "STRING",
                                            question: "Adresse",
                                            longname: "Adresse",
                                            pointer:
                                                "/weitereImmobilien/[i]/immobilieAdresse",
                                        },
                                        {
                                            id: "M7juiFUOoZmkZ2vl",
                                            children: [],
                                            label: "Art der Immobilie",
                                            type: "leafNode",
                                            active: true,
                                            antworttyp: "ENUMERATION_SINGLE",
                                            pointer:
                                                "/weitereImmobilien/[i]/immobilieArt",
                                            question: "Art der Immobilie",
                                            longname: "Art der Immobilie",
                                            connections: {
                                                id: "Ur3ZBFyKWxqUOXe7",
                                                connectionType:
                                                    "singleconnection",
                                                treeName: "enumerations",
                                                origin: {
                                                    children: [
                                                        {
                                                            children: [],
                                                            type: "leafNode",
                                                            label: "Eigentumswohnung",
                                                            value: "WOHNUNG",
                                                            id: "Sv9Q0GQgZsRH8QPq",
                                                        },
                                                        {
                                                            children: [],
                                                            type: "leafNode",
                                                            label: "Eigenes Haus",
                                                            value: "HOME",
                                                            id: "LWLN77XoXn2HxwEO",
                                                        },
                                                        {
                                                            id: "Bsa6lLboFKw50J1p",
                                                            children: [],
                                                            type: "leafNode",
                                                            label: "Ferienhaus",
                                                            value: "FERIENHAUS",
                                                        },
                                                        {
                                                            id: "IzQd6dlRN2OqPNaE",
                                                            children: [],
                                                            type: "leafNode",
                                                            label: "Ferienwohnung",
                                                            value: "FERIENWOHNUNG",
                                                        },
                                                    ],
                                                    type: "innerNode",
                                                    label: "weitereImmobilieArt",
                                                    id: "Ur3ZBFyKWxqUOXe7",
                                                },
                                            },
                                        },
                                        {
                                            children: [],
                                            label: "Immobilie Vermietet?",
                                            type: "leafNode",
                                            active: true,
                                            antworttyp: "YESNO",
                                            pointer:
                                                "/weitereImmobilien/[i]/immobilieVermietetJaNein",
                                            question:
                                                "Ist die Immobilie vermietet?",
                                            longname:
                                                "Ist die Immobilie vermietet?",
                                            id: "XJpble7GbrEl9zWZ",
                                        },
                                        {
                                            children: [],
                                            label: "Heizöltank vorhanden?",
                                            type: "leafNode",
                                            active: true,
                                            antworttyp: "YESNO",
                                            pointer:
                                                "/weitereImmobilien/[i]/immobilieMitHeizoeltankJaNein",
                                            question:
                                                "Hat die Immobilie einen Heizöltank?",
                                            longname:
                                                "Hat die Immobilie einen Heizöltank?",
                                            id: "zZHBE9XXsQuSA7ZX",
                                        },
                                        {
                                            children: [],
                                            label: "Wird die Immobilie finanziert?",
                                            type: "leafNode",
                                            active: true,
                                            antworttyp: "YESNO",
                                            pointer:
                                                "/weitereImmobilien/[i]/immobilieFinanziertJaNein",
                                            question:
                                                "Wird die Immobilie finanziert?",
                                            longname:
                                                "Wird die Immobilie finanziert?",
                                            id: "WI68OoNkvVjx0lEy",
                                        },
                                    ],
                                    label: "Weitere Immobilien",
                                    type: "innerNode",
                                    active: true,
                                    description: "Weitere Immobilien",
                                    longname: "Weitere Immobilien",
                                    pointer: "/weitereImmobilien",
                                    multiple: true,
                                },
                            },
                            {
                                id: "xemtkpUPweNTb8Kw",
                                connectionType: "multiconnection",
                                treeName: "attributes",
                                origin: {
                                    children: [],
                                    label: "Adresse",
                                    type: "leafNode",
                                    active: true,
                                    pointer:
                                        "/kunde/adresse/strasse",
                                    question: "Adresse",
                                    longname: "Adresse",
                                    antworttyp: "STRING",
                                    connections: {
                                        id: "gm4WEFjOqzupDvfA",
                                        connectionType: "singleconnection",
                                        treeName: "enumerations",
                                        origin: {
                                            id: "gm4WEFjOqzupDvfA",
                                            children: [
                                                {
                                                    id: "GZ9PE5mRLv8fQEit",
                                                    children: [],
                                                    type: "leafNode",
                                                    label: "Haus",
                                                    value: "HOME",
                                                },
                                                {
                                                    id: "wyDw9Y1E0JO13JeU",
                                                    children: [],
                                                    type: "leafNode",
                                                    label: "Wohnung",
                                                    value: "WOHNUNG",
                                                },
                                                {
                                                    id: "vOwi6u4ALKFXdRx1",
                                                    children: [],
                                                    type: "leafNode",
                                                    label: "Ohne Hausstand",
                                                    value: "OHNE_HAUSSTAND",
                                                },
                                            ],
                                            type: "innerNode",
                                            label: "wohnsituation",
                                        },
                                    },
                                    id: "xemtkpUPweNTb8Kw",
                                },
                            },
                        ],
                        leistungsmerkmale: [
                            {
                                id: "DKm0ZMdehblCu7i4",
                                treeName: "attributes",
                                origin: {
                                    id: "DKm0ZMdehblCu7i4",
                                    children: [],
                                    label: "Elementarschäden",
                                    type: "leafNode",
                                    active: true,
                                    longname: "Elementarschäden",
                                    pointer: "",
                                    leistungsmerkmal: true,
                                },
                            },
                            {
                                id: "VPsrFyf1F0LXOrLe",
                                treeName: "attributes",
                                origin: {
                                    id: "VPsrFyf1F0LXOrLe",
                                    children: [],
                                    label: "Allgefahrendeckung",
                                    type: "leafNode",
                                    active: true,
                                    longname: "Allgefahrendeckung",
                                    pointer: "",
                                    leistungsmerkmal: true,
                                },
                            },
                            {
                                id: "R21IVCDfdOdPAGrA",
                                treeName: "attributes",
                                origin: {
                                    id: "R21IVCDfdOdPAGrA",
                                    children: [],
                                    label: "Ableitungsrohre",
                                    type: "leafNode",
                                    active: true,
                                    longname: "Ableitungsrohre",
                                    pointer: "",
                                    leistungsmerkmal: true,
                                },
                            },
                        ],
                    },
                ],
                label: "Gebäudeversicherung",
                type: "innerNode",
                active: true,
                longname: "Ihr Haus",
                icon: "/svg/icons--haus--1765285499641-934725335.svg",
                prio: "SEHR WICHTIG",
                important: "SEHR WICHTIG",
                status: "NICHTABGESICHERT",
                newStatus: "LEER",
                leistungsmerkmale: [
                    {
                        id: "DKm0ZMdehblCu7i4",
                        children: [],
                        label: "Elementarschäden",
                        type: "leafNode",
                        active: true,
                        longname: "Elementarschäden",
                        pointer: "",
                        leistungsmerkmal: true,
                    },
                    {
                        id: "VPsrFyf1F0LXOrLe",
                        children: [],
                        label: "Allgefahrendeckung",
                        type: "leafNode",
                        active: true,
                        longname: "Allgefahrendeckung",
                        pointer: "",
                        leistungsmerkmal: true,
                    },
                    {
                        id: "R21IVCDfdOdPAGrA",
                        children: [],
                        label: "Ableitungsrohre",
                        type: "leafNode",
                        active: true,
                        longname: "Ableitungsrohre",
                        pointer: "",
                        leistungsmerkmal: true,
                    },
                ],
                objekt: {
                    strasse: "Hauptstraße 25",
                    postleitzahl: "12345",
                    ort: "Musterstadt",
                    immobilieArt: "HOME",
                    immobilieMitHeizoeltankJaNein: true,
                },
                isSelected: true,
            }

            updateData(payload ?? mock, "/themenfeld/taaobjekt")
            const prueffragen = {
                blitzueberspannung: true,
                fahrlaessigkeit: true,
                diebstahl: true,
                garteninventar: true,
                arbeitsplatz: true,
            }
            updateData(prueffragen, "/taa/prueffragen")
            navigate("/taa")
            updateData(evaluateProducts(), "/taa/produkt/list")
            tarifrechnen()
        },
        ABSICHERUNGSPROFIL_DESELECT_OBJECT: ({payload}) => {
            const angebotsObjektePointer = "/themenfeld/angebotsobjekte"
            const selectedObjekte = getDataFromPointer(angebotsObjektePointer)
            const updatedObjekte = selectedObjekte.filter(
                (entry) => entry.id !== payload.id,
            )
            updateData(updatedObjekte, angebotsObjektePointer)
        },
        TAA_PRODUKT_AUSWAEHLEN: ({payload}) => {
            updateData(payload, "/taa/produkt/selected")
        },
        TAA_FILTER_PRODUKTE: () => {
            const targetProdukte = evaluateProducts()
            updateData(targetProdukte, "/taa/produkt/list")
            tarifrechnen()
        },
        TAA_TARIFRECHNER: tarifrechnen,
        TAA_ABSCHLUSS: () => {
            const sparte = getDataFromPointer("/themenfeld/taaobjekt")
            const produkt = getDataFromPointer("/taa/produkt/selected")
            const newAbsicherungList =
                getDataFromPointer("/absicherung/newList") ?? []

            const taaProdukt = {...produkt, zusatzschutz: {...produkt.zusatzschutz, zahlweise: getDataFromPointer("/taa/angebotsdaten/zahlweise")}}
            const newAbsicherung = {
                id: nanoid(),
                produkt: {id: sparte.id, label: sparte.label},
                objekt: sparte.objekt,
                status: "GUTABGESICHERT",
                taaProdukt,
            }

            updateData(
                [...newAbsicherungList, newAbsicherung],
                "/absicherung/newList",
            )

            const selectedObjekte = getDataFromPointer("/themenfeld/angebotsobjekte").filter(entry => entry.id !== sparte.id || JSON.stringify(entry.objekt) !== JSON.stringify(sparte.objekt))
            updateData(selectedObjekte, "/themenfeld/angebotsobjekte")

            navigate("/absicherungangebot")
        },
        TAA_SWITCH_PRODUCT_DISPLAY: ({value}) => {
            if (value) {
                const allProducts = evaluateProductsInViewAll()
                updateData(allProducts, "/taa/produkt/list")
            } else {
                const displayProducts = evaluateProducts()
                updateData(displayProducts, "/taa/produkt/list")
            }
            tarifrechnen()
        },
    }
}
